import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Button from '@ecp/common/src/components/button/bo/Button';
import { IMAGE_STATUS } from '@ecp/common/src/const/imageFile';

import FormAccordion from '@bo-components/common/form/FormAccordion';
import Form from '@bo-components/common/form/Form';
import FormField from '@bo-components/common/form/FormField';
import ImageFileUploader from '@bo-components/common/ImageFileUploader';
import { TERMINATED } from '../consts/SiteConst';
import SiteImagePcPopup from '../popups/SiteImagePcPopup';
import SiteImageMoPopup from '../popups/SiteImageMoPopup';
import * as moConst from '../consts/SiteImageMoPopupConst';
import * as pcConst from '../consts/SiteImagePcPopupConst';

const LabelText = styled(TextBox)`
  height: 100%;
  width: 100%;
  font-weight: ${boTheme.font.weight.bold};
  font-size: ${boTheme.font.size.small};
  color: ${boTheme.color.sub1};
  border-left: 1px solid ${boTheme.color.table1};
  justify-content: center;
`;

const ContentArea = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  gap: 14px;
  border-left: 1px solid ${boTheme.color.table1};
  padding: 14px;
`;

const LogoButton = styled(Button)`
  min-width: 121px;
  position: absolute;
  right: 10px;
`;

const ripSize = (itemList) =>
  itemList.map((item) => ({
    ...item,
    desc: item.desc.split('\n')[0],
  }));

const DetailImageInfo = ({ siteBasicInfo, welmallInfo, refreshSiteInfo }) => {
  const [showPCImagePopup, setShowPCImagePopup] = useState(false);
  const [showMoImagePopup, setShowMoImagePopup] = useState(false);

  const pcLogoImageFileList = useMemo(
    () => [
      {
        type: 'pcLogoTabImgFileId',
        url: siteBasicInfo.pcLogoTabImgFilePath,
        status: IMAGE_STATUS.REGISTERED,
      },
      { type: 'pcGnbImgFileId', url: siteBasicInfo.pcGnbImgFilePath, status: IMAGE_STATUS.REGISTERED },
      {
        type: 'pcFooterImgFileId',
        url: siteBasicInfo.pcFooterImgFilePath,
        status: IMAGE_STATUS.REGISTERED,
      },
    ],
    [siteBasicInfo]
  );

  const mobileLogoImageFileList = useMemo(
    () => [
      {
        type: 'mobileGnbImgFileId',
        url: siteBasicInfo.mobileGnbImgFilePath,
        status: IMAGE_STATUS.REGISTERED,
      },
      { type: 'mobileFooterImgFileId', url: siteBasicInfo.mobileFooterImgFilePath, status: IMAGE_STATUS.REGISTERED },
    ],
    [siteBasicInfo]
  );

  return (
    <>
      <FormAccordion
        label={'로고 이미지, 프로모션 배너 관리'}
        description={'PC, Mobile의 로고, 프로모션 배너를 관리합니다.'}
        initialOpen
        buttonless
        noFold
      >
        <FlexBox width={'100%'}>
          <Form column={1}>
            <FormField label={'구분'} valueStyle={{ backgroundColor: `${boTheme.color.table2}`, padding: 0, gap: 0 }}>
              <FlexBox width={'100%'} height={'100%'} align-items={'center'}>
                <LabelText>PC</LabelText>
                <LogoButton
                  type={'Third'}
                  disabled={siteBasicInfo.siteStatCode === TERMINATED}
                  onClick={() => setShowPCImagePopup(true)}
                >
                  로고및배너수정
                </LogoButton>
              </FlexBox>
              <FlexBox width={'100%'} height={'100%'} align-items={'center'}>
                <LabelText>Mobile</LabelText>
                <LogoButton
                  type={'Third'}
                  disabled={siteBasicInfo.siteStatCode === TERMINATED}
                  onClick={() => setShowMoImagePopup(true)}
                >
                  로고및배너수정
                </LogoButton>
              </FlexBox>
            </FormField>
            <FormField label={'LOGO (GNB, Footer)'} valueStyle={{ padding: 0, gap: 0 }}>
              <ContentArea>
                <ImageFileUploader
                  readOnly
                  imageFileList={pcLogoImageFileList}
                  imageItemList={ripSize(pcConst.pcImageItemList())}
                />
              </ContentArea>
              <ContentArea>
                <ImageFileUploader
                  readOnly
                  imageFileList={mobileLogoImageFileList}
                  imageItemList={ripSize(moConst.mobileImageItemList())}
                />
              </ContentArea>
            </FormField>
            <FormField label={'로그인'} valueStyle={{ padding: 0, gap: 0 }}>
              <ContentArea>
                <ImageFileUploader
                  readOnly
                  imageFileList={[
                    {
                      type: 'pcLoginImgFileId',
                      url: siteBasicInfo.pcLoginImgFilePath,
                      status: IMAGE_STATUS.REGISTERED,
                    },
                  ]}
                  imageItemList={ripSize(pcConst.loginLogoImageItemList())}
                />
                <FlexBoxColumn align-items={'flex-start'}>
                  <TextBox color={'#666666'}>문구</TextBox>
                  <TextBox style={{ wordBreak: 'break-all' }}>{siteBasicInfo.pcLoginPhrase}</TextBox>
                </FlexBoxColumn>
              </ContentArea>
              <ContentArea>
                <ImageFileUploader
                  readOnly
                  imageFileList={[
                    {
                      type: 'mobileLoginImgFileId',
                      url: siteBasicInfo.mobileLoginImgFilePath,
                      status: IMAGE_STATUS.REGISTERED,
                    },
                  ]}
                  imageItemList={ripSize(moConst.loginLogoImageItemList())}
                />
                <FlexBoxColumn align-items={'flex-start'}>
                  <TextBox color={'#666666'}>문구</TextBox>
                  <TextBox style={{ wordBreak: 'break-all' }}>{siteBasicInfo.mobileLoginPhrase01}</TextBox>
                  <TextBox style={{ wordBreak: 'break-all' }}>{siteBasicInfo.mobileLoginPhrase02}</TextBox>
                </FlexBoxColumn>
              </ContentArea>
            </FormField>
            <FormField label={'대량구매'} valueStyle={{ padding: 0, gap: 0 }}>
              <ContentArea>
                <ImageFileUploader
                  readOnly
                  imageFileList={[
                    {
                      type: 'pcBulkpImgFileId',
                      url: siteBasicInfo.pcBulkpImgFilePath,
                      status: IMAGE_STATUS.REGISTERED,
                    },
                  ]}
                  imageItemList={ripSize(pcConst.bulkLogoImageItemList())}
                />
                <FlexBoxColumn align-items={'flex-start'}>
                  <TextBox color={'#666666'}>문구</TextBox>
                  <TextBox style={{ wordBreak: 'break-all' }}>{siteBasicInfo.pcBulkpPhrase}</TextBox>
                </FlexBoxColumn>
              </ContentArea>
              <ContentArea />
            </FormField>
            <FormField label={'프로모션배너'} valueStyle={{ padding: 0, gap: 0 }}>
              <ContentArea>
                <ImageFileUploader
                  readOnly
                  imageFileList={[
                    {
                      type: 'pcPromoBannerImgFileId',
                      url: siteBasicInfo.pcPromoBannerImgFilePath,
                      status: IMAGE_STATUS.REGISTERED,
                    },
                  ]}
                  imageItemList={ripSize(pcConst.bannerImageItemList())}
                />
              </ContentArea>
              <ContentArea />
            </FormField>
            <FormField label={'파비콘'} valueStyle={{ padding: 0, gap: 0 }}>
              <ContentArea>
                <ImageFileUploader
                  readOnly
                  text={''}
                  imageFileList={[
                    {
                      type: 'pcFicoImgFileId',
                      url: siteBasicInfo.pcFicoImgFilePath,
                      status: IMAGE_STATUS.REGISTERED,
                    },
                  ]}
                  imageItemList={ripSize(pcConst.ficoImageItemList())}
                  cardProps={{ width: '20px', height: '20px' }}
                />
              </ContentArea>
              <ContentArea />
            </FormField>
          </Form>
        </FlexBox>
      </FormAccordion>
      <SiteImagePcPopup
        open={showPCImagePopup}
        onClose={() => setShowPCImagePopup(false)}
        siteInfo={siteBasicInfo}
        welmallInfo={welmallInfo}
        refreshSiteInfo={refreshSiteInfo}
      />
      <SiteImageMoPopup
        open={showMoImagePopup}
        onClose={() => setShowMoImagePopup(false)}
        siteInfo={siteBasicInfo}
        welmallInfo={welmallInfo}
        refreshSiteInfo={refreshSiteInfo}
      />
    </>
  );
};

export default DetailImageInfo;

DetailImageInfo.propTypes = {
  siteBasicInfo: PropTypes.object,
  welmallInfo: PropTypes.object,
  refreshSiteInfo: PropTypes.func,
};
